/**
 * Per-turn reply-language hint, ported from suppy-agent. Small models drift
 * between scripts (an Arabizi message answered in Arabic script, an English
 * one answered in Arabizi); a deterministic reminder naming the language of
 * THIS message fixes most of it. Dialect and register come from the
 * retailer's config rules, so nothing here is country-specific.
 *
 * A message's language is weighed, never decided by one token: its Arabizi
 * signs (digits used as letters, Levantine words) against its English or
 * French words. A message that leans neither way ("add 7up", "ok", a product
 * name) has no language of its own, and the conversation keeps the one it has
 * (callers fall back to `Conversation.language`, which moves only on a message
 * that clearly has one). One "5th" once flipped an English chat to Arabizi
 * buttons and the Arabizi model (2026-10-30).
 */

const ARABIC_SCRIPT = /[؀-ۿ]/;
// A digit used as a letter inside or at the start of a word: 7aleeb, ba2a, 3al, sa7en.
// Unit and ordinal suffixes are excluded so "2.5kg", "5pcs" or "the 5th one" is not one.
const ARABIZI =
	/(?<![A-Za-z0-9.,])[235789](?!(?:kg|gr|gm|ml|cl|ltr|lt|gb|tb|mm|cm|pcs|pc|oz|lb|mah|pack|st|nd|rd|th)\b)[a-z]{2,}|[a-z]+[235789][a-z]+/gi;
const WORD = /\p{L}+/gu;
// Levantine words with no English or French reading. Only about half of real
// Arabizi messages carry a digit (WhatsApp corpus, 481 writers), so "merci
// ktir, hayda kel shi" must still read as Arabizi, not French.
const ARABIZI_WORDS = new Set([
	"shu",
	"shou",
	"chou",
	"sho",
	"kif",
	"kifak",
	"kifik",
	"kifkon",
	"ktir",
	"kteer",
	"hayda",
	"hayde",
	"hek",
	"heik",
	"yalla",
	"yala",
	"khalas",
	"akid",
	"akeed",
	"leh",
	"lesh",
	"lech",
	"wen",
	"wein",
	"mesh",
	"mish",
	"mich",
	"eza",
	"iza",
	"eno",
	"enno",
	"kamen",
	"kaman",
	"badak",
	"baddak",
	"bade",
	"badde",
	"baddi",
	"badna",
	"bado",
	"tene",
	"tani",
	"lyom",
	"bukra",
	"boukra",
	"bokra",
	"halib",
	"jebne",
	"zid",
	"zidle",
	"zidli",
	"shil",
	"shilo",
	"tekram",
	"sahtein",
	"sa7tein",
	"ahla",
	"marhaba",
	"sabaho",
	"walaw",
	"mnih",
	"tayeb",
	"tayyeb",
	"hala2",
	"ma3",
	"shway",
	"shwaye",
	"ba3den",
	"lezem",
	"lazem",
	"fik",
	"fike",
	"fina",
	"3ana",
	"nehna",
	"enta",
	"ente",
	"hone",
	"hon",
	"jeye",
	"wosel",
	"wasel",
	"talab",
	"se3a",
	"aymta",
	"ade",
	"adde",
	"mafi",
	"kermel",
	"minshan",
]);

const LATIN_LANGUAGES: Record<string, ReadonlySet<string>> = {
	English: new Set([
		"the",
		"do",
		"you",
		"have",
		"i",
		"want",
		"please",
		"add",
		"my",
		"is",
		"what",
		"need",
		"some",
		"and",
		"show",
		"me",
	]),
	French: new Set([
		"vous",
		"est",
		"les",
		"des",
		"avez",
		"je",
		"une",
		"pour",
		"bonjour",
		"du",
		"merci",
		"voudrais",
		"ajoute",
		"avec",
	]),
	Spanish: new Set([
		"tienen",
		"hola",
		"tiene",
		"quiero",
		"los",
		"las",
		"por",
		"gracias",
		"necesito",
		"hay",
	]),
	German: new Set([
		"habt",
		"ihr",
		"haben",
		"sie",
		"ich",
		"und",
		"das",
		"der",
		"die",
		"möchte",
		"hallo",
		"danke",
		"gibt",
	]),
	Italian: new Set(["avete", "ciao", "vorrei", "della", "che", "grazie"]),
	Portuguese: new Set(["vocês", "têm", "olá", "queria", "obrigado", "tem"]),
	Turkish: new Set([
		"var",
		"mı",
		"misiniz",
		"merhaba",
		"istiyorum",
		"lütfen",
	]),
};

export type MessageLanguage =
	| { kind: "arabic" }
	| { kind: "arabizi" }
	| { kind: "latin"; language: string | null }
	| { kind: "other-script" }
	| { kind: "no-words" };

/**
 * What a message is written in. Arabic script decides alone; otherwise its Arabizi signs are
 * counted against the words of the Latin language it reads most like, and the larger wins. A
 * tie is `latin` with no language: the message has none of its own.
 */
export function detectMessageLanguage(message: string): MessageLanguage {
	if (ARABIC_SCRIPT.test(message)) {
		return { kind: "arabic" };
	}
	const words = message.toLowerCase().match(WORD) ?? [];
	const arabizi =
		(message.match(ARABIZI)?.length ?? 0) +
		words.filter((w) => ARABIZI_WORDS.has(w)).length;
	if (words.length === 0) {
		return arabizi > 0 ? { kind: "arabizi" } : { kind: "no-words" };
	}
	if (!/[a-zÀ-ɏ]/i.test(words.join(""))) {
		return { kind: "other-script" };
	}
	let best: string | null = null;
	let bestHits = 0;
	for (const [language, stopwords] of Object.entries(LATIN_LANGUAGES)) {
		const hits = words.filter((w) => stopwords.has(w)).length;
		if (hits > bestHits) {
			best = language;
			bestHits = hits;
		}
	}
	if (arabizi > bestHits) {
		return { kind: "arabizi" };
	}
	return { kind: "latin", language: arabizi === bestHits ? null : best };
}

/** Inbox label for a message's language ("Arabic", "Arabizi", "French"), or null when unclear. */
export function languageLabel(message: string): string | null {
	const detected = detectMessageLanguage(message);
	switch (detected.kind) {
		case "arabic":
			return "Arabic";
		case "arabizi":
			return "Arabizi";
		case "latin":
			return detected.language;
		default:
			return null;
	}
}

/**
 * The language a BCP 47 locale names (the app's language), as the retailer's supported list
 * spells it when it has it ("ar-AE" -> "Lebanese Arabic"), else in English; null when unknown.
 */
export function localeLanguage(
	locale: string | null | undefined,
	supported: readonly string[],
): string | null {
	const base = locale?.toLowerCase().split("-")[0];
	if (!base) {
		return null;
	}
	let name: string | undefined;
	try {
		name = new Intl.DisplayNames(["en"], { type: "language" }).of(base);
	} catch {
		// "en_us" and other malformed tags: the chat body takes any 2-16 characters.
		return null;
	}
	if (!name || name.toLowerCase() === base) {
		return null;
	}
	return (
		supported.find((language) =>
			language.toLowerCase().split(/\s+/).includes(name.toLowerCase()),
		) ?? name
	);
}

export function languageHint(
	message: string,
	fallbackLanguage: string,
	/** The app's language (`localeLanguage`): what an ambiguous message is answered in. */
	appLanguage?: string | null,
	/** The conversation's language so far: an ambiguous message keeps it, before the app's. */
	conversationLanguage?: string | null,
): string {
	const detected = detectMessageLanguage(message);
	const usual = conversationLanguage ?? appLanguage;
	switch (detected.kind) {
		case "arabic":
			return "THIS TURN: the shopper wrote in Arabic script. Reply in Arabic script only (product and brand names stay as in the catalog).";
		case "arabizi":
			return "THIS TURN: the shopper wrote in Arabizi. Reply in Arabizi (Latin letters and digits), no Arabic script.";
		case "latin":
			return detected.language
				? `THIS TURN: the shopper wrote in ${detected.language}. Reply in ${detected.language} only, no Arabic script, no Arabizi.`
				: usual
					? `THIS TURN: reply in the language of the shopper's message when it clearly has one; when it is only a product name, a short reply or words of two languages mixed, keep replying in ${usual}, the language of ${conversationLanguage ? "this conversation" : "their app"}.`
					: "THIS TURN: the shopper wrote in Latin script. Reply in the same language as their message, no Arabic script, no Arabizi.";
		case "other-script":
			return "THIS TURN: reply in the language and script of the shopper's message.";
		case "no-words":
			return `THIS TURN: the message has no words. Reply in ${usual ?? fallbackLanguage}, one friendly line.`;
	}
}
